import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Spinner } from "@/components/Spinner/Spinner";
import { Toggle } from "@/components/Toggle/Toggle";
import { GenerationPhase } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import {
	DRAFTS_SLOW_AFTER_MS,
	formatElapsed,
	isTakingLong,
	useNow,
} from "@/utils/generationTiming";

import { CopywritingStage, STAGE_LABELS, STAGE_ORDER } from "./types";

interface GenerationStatusProps {
	isGenerating: boolean;
	alreadyInProgress: boolean;
	currentStage: CopywritingStage | null;
	currentStageLabel: string | null;
	error: string | null;
	hasConversations: boolean;
	freshStartOverride: boolean;
	handleFreshStart: () => void;
	/** Queued or running while generating; null while the request itself is in flight (PREC-4052). */
	generationPhase?: GenerationPhase | null;
	/** When the current phase began (epoch ms). */
	phaseSince?: number | null;
	/** The error is a refusal to spend: a retry cannot succeed until credits are added. */
	errorIsCredits?: boolean;
	/** Starts the generation again after a failure that a retry can fix. */
	onRetry?: () => void;
}

export const GenerationStatus: FunctionComponent<GenerationStatusProps> = ({
	isGenerating,
	alreadyInProgress,
	currentStage,
	currentStageLabel,
	error,
	hasConversations,
	freshStartOverride,
	handleFreshStart,
	generationPhase = null,
	phaseSince = null,
	errorIsCredits = false,
	onRetry,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, string>) =>
		t(`messages.generateBar.status.${key}`, options);
	const now = useNow(isGenerating && phaseSince !== null);
	const elapsed = phaseSince !== null ? formatElapsed(now - phaseSince) : null;
	const takingLong = isGenerating && isTakingLong(phaseSince, now, DRAFTS_SLOW_AFTER_MS);
	const isQueued = generationPhase === "queued";

	// Before the first stage event arrives (socket still connecting, or the
	// backend is inside a long silent phase like the inline deep research),
	// show a small head start so the bar reads as "started" rather than stuck.
	const stageProgress = currentStage
		? Math.round(((STAGE_ORDER.indexOf(currentStage) + 1) / STAGE_ORDER.length) * 100)
		: 5;
	const runningLabel =
		currentStageLabel ||
		(currentStage ? STAGE_LABELS[currentStage] : null) ||
		ts(generationPhase === "running" ? "generating" : "preparing");

	return (
		<>
			{/* Fresh start (shown when lead has conversations) */}
			{hasConversations && (
				<div className="flex items-center gap-2 pt-1 border-t border-border-light">
					<Toggle
						disabled={isGenerating}
						isChecked={freshStartOverride}
						onChange={handleFreshStart}
					/>
					<span className="text-sm text-text">{ts("freshStart")}</span>
				</div>
			)}

			{/* Queued: waiting for a slot in the company's lane, not yet picked up (PREC-4052) */}
			{isGenerating && isQueued && (
				<div
					className="flex items-center gap-2 pt-2 border-t border-border-light"
					data-testid="generation-queued"
				>
					<span className="h-2 w-2 animate-pulse rounded-full bg-text-tertiary" />
					<span className="text-xs text-text-meta">
						{elapsed ? ts("queued", { elapsed }) : ts("buttonQueued")}
					</span>
				</div>
			)}

			{/* Running: shown for the whole run, with a fallback label until the first stage event */}
			{isGenerating && !isQueued && (
				<div className="flex flex-col gap-1.5 pt-2 border-t border-border-light">
					<div className="flex items-center gap-2">
						<Spinner color="#4d66f5" />
						<span className="text-xs text-text-meta">
							{elapsed ? ts("elapsed", { label: runningLabel, elapsed }) : runningLabel}
						</span>
					</div>
					<div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
						<div
							className="h-full bg-blue-action rounded-full transition-all duration-500"
							style={{ width: `${stageProgress}%` }}
						/>
					</div>
				</div>
			)}

			{takingLong && (
				<div className="text-xs text-text-meta" data-testid="generation-taking-long">
					{ts(isQueued ? "slowQueued" : "slowRunning")}
				</div>
			)}

			{/* Already-in-progress notice (409) */}
			{alreadyInProgress && (
				<div className="text-xs text-text-meta pt-1">{ts("alreadyInProgress")}</div>
			)}

			{/* Error: a credits refusal says where to add credits; anything else can be retried */}
			{error && !alreadyInProgress && (
				<div className="flex items-center gap-2 pt-1 text-xs" data-testid="generation-error">
					<span className="text-status-error">
						{errorIsCredits ? ts("notEnoughCredits") : error}
					</span>
					{!errorIsCredits && onRetry && (
						<button
							className="cursor-pointer text-blue-action underline"
							type="button"
							onClick={onRetry}
						>
							{ts("tryAgain")}
						</button>
					)}
				</div>
			)}
		</>
	);
};
